import { ReloadButton } from "@/components/ReloadButton";
import { LoadingContent } from "@/components/LoadingContent";
import { createFileRoute } from "@tanstack/react-router";
import { Anchor, Box, Button, Code, Container, List, Stack, Text, TextInput, Title } from "@mantine/core";
import type { ReaderValues } from "@/features/route-readers/types";
import { readerPages } from "@/features/route-readers/pages";
import { useReaderResponses } from "@/features/route-readers/hooks";
import { readerRecordLinks, readerSearch, resolveReaderPath } from "@/features/route-readers/requests";

function Page() {
  const page = readerPages["/trips/:id/import-activity"]!;
  const values: ReaderValues = { ...Route.useSearch(), ...Route.useParams() };
  const responses = useReaderResponses(page, values);
  const related = Object.values(readerPages).filter((entry) =>
    entry.path !== page.path && entry.path.startsWith(page.path.replace(/\/:.*$/, "") + "/"),
  );
  return (
    <Container size="xl" py="xl">
      <Stack>
        <Title order={1}>{page.title}</Title>
        <Code style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere", wordBreak: "break-word", minWidth: 0, maxWidth: "100%" }}>{page.path}</Code>
        {page.note ? <Text>{page.note}</Text> : null}
        {page.inputs.length ? (
          <Box component="form" method="get">
            <Stack>
              {page.inputs.map((name) => <TextInput key={name} name={name} label={name} defaultValue={values[name] ?? ""} />)}
              <Button type="submit">Apply query</Button>
            </Stack>
          </Box>
        ) : null}
        {page.local ? <Code block mah={400} tabIndex={0} style={{ overflowY: "auto", whiteSpace: "pre-wrap", overflowWrap: "anywhere", wordBreak: "break-word", minWidth: 0, maxWidth: "100%" }}>{JSON.stringify({ ...page.local, parameters: values }, null, 2)}</Code> : null}


        {responses.map(({ request, url, query }) => (
          <Stack key={request.label} gap="xs">
            <Title order={2}>{request.label}</Title>
            <Code block style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere", wordBreak: "break-word", minWidth: 0, maxWidth: "100%" }}>{url ?? request.path}</Code>
            {url === null ? <Text>Supply the required identifier, or wait for its lookup response.</Text> : (
              <>
                <ReloadButton resource={request.label.toLowerCase()} loading={query.isFetching} onReload={() => query.refetch()} />
                {query.isPending ? <LoadingContent label="Loading response…" variant="rows" /> : null}
                {query.error ? <Code block mah={400} tabIndex={0} style={{ overflowY: "auto", whiteSpace: "pre-wrap", overflowWrap: "anywhere", wordBreak: "break-word", minWidth: 0, maxWidth: "100%" }}>{JSON.stringify({ error: query.error.message }, null, 2)}</Code> : null}
                {query.data ? (
                  <>
                    <Text>HTTP {query.data.status}</Text>
                    <List>{readerRecordLinks(request.path, query.data.body, values).map((link) => <List.Item key={link.href}><Anchor href={link.href}>{link.label}</Anchor></List.Item>)}</List>
                    <Code block mah={400} tabIndex={0} style={{ overflowY: "auto", whiteSpace: "pre-wrap", overflowWrap: "anywhere", wordBreak: "break-word", minWidth: 0, maxWidth: "100%" }}>{JSON.stringify(query.data.body, null, 2)}</Code>
                  </>
                ) : null}
              </>
            )}
          </Stack>
        ))}
        {related.length ? <List>{related.map((entry) => {
          const href = resolveReaderPath(entry.path, values);
          return <List.Item key={entry.path}>{href ? <Anchor href={href}>{entry.path}</Anchor> : <Code style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere", wordBreak: "break-word", minWidth: 0, maxWidth: "100%" }}>{entry.path}</Code>}</List.Item>;
        })}</List> : null}
      </Stack>
    </Container>
  );
}

export const Route = createFileRoute("/_authenticated/_protected/trips/$id/import-activity")({ validateSearch: readerSearch, component: Page });
